/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import {Millisecond} from '../../units/Units';
import {EncodedEdgeToken} from '../../edgeAuth/EncodedEdgeToken';
import FeatureEnablement from '../../environment/FeatureEnablement';
import SDK from '../SDK';
import {IEncodedStreamSink} from '../transformation/EncodedStreamSink';
import {IStreamTrackTransform} from '../transformation/StreamTrackTransform';
import Channel, {ChannelOptions} from './Channel';
import {IOnRealTimeSubscribeRequestCallback} from './IOnRealTimeSubscribeRequestCallback';
import {IOnRealTimeSubscribeResponseCallback} from './IOnRealTimeSubscribeResponseCallback';

export type CreateChannelOptions = {
  videoElement: HTMLVideoElement;
  token: EncodedEdgeToken;
  targetLag?: Millisecond;
  videoStreamTransformCallback?: IStreamTrackTransform<VideoFrame>;
  audioStreamTransformCallback?: IStreamTrackTransform<AudioData>;
  encodedVideoStreamSink?: IEncodedStreamSink<RTCEncodedVideoFrame>;
  encodedAudioStreamSink?: IEncodedStreamSink<RTCEncodedAudioFrame>;
};

export default class Channels {
  static createChannel(options: CreateChannelOptions): Channel {
    if (!SDK.initialized.value) {
      throw new Error('SDK is not loaded.');
    }

    const channelOptions: ChannelOptions = {
      targetLag: options.targetLag,
      videoStreamTransformCallback: options.videoStreamTransformCallback,
      audioStreamTransformCallback: options.audioStreamTransformCallback,
      encodedVideoStreamSink: options.encodedVideoStreamSink,
      encodedAudioStreamSink: options.encodedAudioStreamSink
    };
    const optionsWithRealTimeSubscribeCallbacks: ChannelOptions & {
      onRealTimeSubscribeRequestCallback?: IOnRealTimeSubscribeRequestCallback;
      onRealTimeSubscribeResponseCallback?: IOnRealTimeSubscribeResponseCallback;
    } = options;

    if (optionsWithRealTimeSubscribeCallbacks.onRealTimeSubscribeRequestCallback) {
      channelOptions.onRealTimeSubscribeRequestCallback = optionsWithRealTimeSubscribeCallbacks.onRealTimeSubscribeRequestCallback;
    }

    if (optionsWithRealTimeSubscribeCallbacks.onRealTimeSubscribeResponseCallback) {
      channelOptions.onRealTimeSubscribeResponseCallback = optionsWithRealTimeSubscribeCallbacks.onRealTimeSubscribeResponseCallback;
    }

    return new Channel(options.videoElement, options.token, channelOptions);
  }

  public static get isFrameTimingInformationSupported(): boolean {
    return FeatureEnablement.isChannelFrameTimingInformationSupported;
  }

  private constructor() {
    throw new Error('Channels is a static class that may not be instantiated');
  }
}